import React from 'react';
import styled from 'styled-components';
import {getBowserState} from '@epic-core/common';
import {ContextMessage} from '@epic-mw/localization';
import {
    Button,
    Typography,
    VerticalSpacer,
    GridContainer,
    EyebrowProps,
    Image,
    CmsButtonProps,
    ImageProps,
    ScrollIndicator
} from '../../elements';
import {AnimateOnViewWrap, WithCaption, WithCaptionProps} from '../../wraps';
import {AccentColorOptions, useBreakpointDown} from 'epic-ue-theme';
import {Grid} from '@material-ui/core';
import {SocialIconsStacked} from '../../elements/icons/socials/SocialIconsStacked';
const Styled = styled.div<{img?: string; }>`
    position: relative;

    .MuiGrid-item {
        padding: 0px;
    }

    .hero-text-wrapper {
        position: relative;
        color: ${(p) => p.theme.palette.text.primary};

        background-color: ${(p) =>
            p.theme.ifLight(p, p.theme.palette.common.white, p.theme.palette.background.paper)};
        ${(p) => p.theme.breakpoints.up('lg')} {
            .h2 span {
                font-size: 28px;
                line-height: 38px;
            }
        }
    }

    .hero-title {
        text-transform: uppercase;
    }

    .hero-img {
        position: relative;
        top: -72px;
        margin-bottom: -72px;
    }

    .stackedSocialIcons {
        position: relative;
        top: 13px;
    }

    &&.ue-image-hero.light {
        .hero-text-wrapper {
            color: ${(p) => p.theme.palette.common.white};
        }
        .social-icon-grid svg {
            color: ${(p) => p.theme.palette.common.white};
        }
        .scroll-indicator span {
            background: white;
        }
    }

    .second-button {
        margin-left: 32px;
        ${(p) => p.theme.breakpoints.down('xs')} {
            margin-left: unset;
            margin-top: 32px;
            display: block;
        }
    }

    .scroll-indicator{
        height: 80px;
    }
`;

export interface HeroBasicProps {
    eyebrow?: EyebrowProps;
    title?: string;
    description?: string;
    dateStr?: string;
    button1?: CmsButtonProps;
    button2?: CmsButtonProps;
    blockMobileDownload?: boolean;
    image?: ImageProps;
    caption?: WithCaptionProps;
    accentColor?: AccentColorOptions;
    fontTheme?: 'light' | 'dark' | '';
    showScrollIndicator?: boolean;
}

export const HeroBasic = ({
    eyebrow,
    title,
    description,
    dateStr,
    button1 = {},
    button2 = {},
    blockMobileDownload,
    image,
    caption,
    accentColor,
    fontTheme = '',
    showScrollIndicator
}: HeroBasicProps): JSX.Element | null => {
    const {mobile, tablet} = getBowserState();
    const isSm = useBreakpointDown('sm');
    const leftPanelGridCols = isSm ? 12 : 11;

    return (
        <Styled className={`ue-image-hero ${fontTheme}`} img={image?.src}>
            <div className="hero-text-wrapper">
                <VerticalSpacer variant="section" />
                <GridContainer>
                    <Grid
                        container
                        direction="row"
                        justifyContent={'flex-start'}
                        alignItems="stretch"
                    >
                        <Grid item md={leftPanelGridCols}>
                            {eyebrow && eyebrow.text && (
                                <>
                                    <Grid item>
                                        <AnimateOnViewWrap translateY={20}>
                                            <Typography variant="h4" big>
                                                {eyebrow.text}
                                            </Typography>
                                        </AnimateOnViewWrap>
                                        <VerticalSpacer variant="custom" spacing={4} />
                                    </Grid>
                                </>
                            )}
                            {title && (
                                <>
                                    <Grid item md={9} >
                                        {!eyebrow && <VerticalSpacer variant="heading" />}
                                        <AnimateOnViewWrap delay={0.15} translateY={15}>
                                            <Typography className="hero-title" variant="h1" petit>
                                                {title}
                                            </Typography>
                                        </AnimateOnViewWrap>
                                    </Grid>
                                </>
                            )}

                            {description && (
                                <>
                                    <Grid item md={7}>
                                        {isSm ? (
                                            <VerticalSpacer variant="eyebrow" />
                                        ) : (
                                            <VerticalSpacer variant="custom" spacing={3} />
                                        )}
                                        <AnimateOnViewWrap delay={0.3} translateY={10}>
                                            <Typography variant="h2">{description}</Typography>
                                        </AnimateOnViewWrap>
                                    </Grid>
                                </>
                            )}
                            {dateStr && (
                                <Grid item>
                                    <div className="author-date">
                                        {isSm ? (
                                            <VerticalSpacer variant="eyebrow" />
                                        ) : (
                                            <VerticalSpacer variant="custom" spacing={3} />
                                        )}
                                        <AnimateOnViewWrap delay={0.45} translateY={10}>
                                            <Typography variant="h3">{dateStr}</Typography>
                                        </AnimateOnViewWrap>
                                    </div>
                                </Grid>
                            )}
                            {(mobile || tablet) && blockMobileDownload ? (
                                <Typography variant="h4" big>
                                    <VerticalSpacer variant="custom" spacing={4} />
                                    <ContextMessage code="epic.launcher.download.mobile.tips" />
                                </Typography>
                            ) : button1 && button1.href ? (
                                <Grid item>
                                    <VerticalSpacer variant="custom" spacing={4} />
                                    <AnimateOnViewWrap delay={0.6} translateY={10}>
                                        <Button
                                            type={button1.type || 'primary'}
                                            href={button1.href}
                                            color={accentColor}
                                        >
                                            {button1.label || 'Learn more'}
                                        </Button>
                                        {button2 && button2.href ? (
                                            <Button
                                                className="second-button"
                                                type={button2.type || 'secondary'}
                                                href={button2.href}
                                                color={accentColor}
                                            >
                                                {button2.label || 'Learn more'}
                                            </Button>
                                        ) : (
                                            ''
                                        )}
                                    </AnimateOnViewWrap>
                                </Grid>


                            ) : (
                                ''
                            )}

                            {showScrollIndicator && <Grid item>
                                <VerticalSpacer variant="custom" spacing={6} />
                            <ScrollIndicator forceSmall={true} />
                            </Grid>}
                        </Grid>
                        {!isSm && (
                            <Grid className="stackedSocialIcons" item md={1}>
                                <SocialIconsStacked accentColor={accentColor} iconColor={fontTheme === 'light' ? 'inherit' : undefined} />
                            </Grid>
                        )}

                        <>
                            <VerticalSpacer variant="section" />
                            {isSm && image?.src ? (
                                <VerticalSpacer variant="feature" />
                            ) : (
                                image?.src && <VerticalSpacer variant="feature" />
                            )}</>

                    </Grid>
                </GridContainer>
            </div>

            <GridContainer>
                {image && image.src ? (
                    <div className="hero-img">
                        <AnimateOnViewWrap translateY={20}>
                            <WithCaption {...caption}>
                                <Image disableRatioHelper {...image} />
                            </WithCaption>
                        </AnimateOnViewWrap>
                    </div>
                ) : (
                    ''
                )}
            </GridContainer>

        </Styled>
    );
};
